// BTEmailEditor.tsx
import { useRef } from 'react';
import { TextField } from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../../css/quillstyles.css';
import { HorizontalLayout } from '../../common-layout';
import BTBox from '../BTBox';
import BTButton from '../BTButton';
import theme from '../../theme';
import { dialogStore } from '../../../store/DialogStore';
import { useNavigate } from 'react-router-dom';

type BTEmailEditorProps = {
    title: string;
    contents: string;
    setTitle: (value: string) => void;
    setContents: (value: string) => void;
    onSave: () => void;
}

const BTEmailEditor = ({
    title,
    contents,
    setTitle,
    setContents,
    onSave,
}: BTEmailEditorProps) => {
    const navigate = useNavigate();
    const quillRef = useRef<ReactQuill>(null);

    const modules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike',],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: [] }], // 정렬 옵션 추가
            [{ color: [] }, { background: [] }],
            ['link', 'image'],
            ['clean'],
        ],
        imageActions: {}, // @xeger/quill-image-actions 모듈 활성화
        imageFormats: {}, // @xeger/quill-image-formats 모듈 활성화
    };

    const formats: string[] = [
        'header',
        'bold',
        'italic',
        'underline',
        'strike',
        'list',
        'bullet',
        'align', // 정렬 추가
        'link',
        'color',  // 색상 포맷 추가
        'background',  // 배경색 포맷 추가
        'image',
        'float',
        'width',
        'height',
    ];

    const checkRequiredInputs = (): boolean => {
        if (!title.trim()) {
            dialogStore.showDialog({
                title: '입력 필요',
                message: '제목을 입력해주세요.',
                onConfirm: () => { },
            });
            return false;
        }

        if (!contents.trim() || contents === '<p><br></p>') {
            dialogStore.showDialog({
                title: '입력 필요',
                message: '내용을 입력해주세요.',
                onConfirm: () => { },
            });
            return false;
        }
        return true;
    }

    const handleSave = () => {
        if (!checkRequiredInputs()) return;
        onSave();
    };

    const handleCancel = (): void => {
        dialogStore.showDialog({
            title: '확인',
            message: '정말 그만두시겠습니까?',
            onConfirm: () => {
                navigate(-1);
            },
            onCancel: () => {
                console.log('취소');
            }
        });
    };

    return (
        <form noValidate autoComplete="off" onSubmit={(e) => e.preventDefault()}>
            {/* 제목 */}
            <div
                style={{
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                    marginTop: "8px",
                    fontSize: "14px",
                    color: "#6C757D",
                    width: "100%"
                }}
            >
                <div
                    style={{
                        fontWeight: "700",
                        minWidth: "48px",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                    }}
                >
                    제목
                </div>
                <div style={{ flex: 1 }}>
                    <TextField
                        label="제목"
                        variant="outlined"
                        fullWidth
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                </div>
            </div>

            {/* 본문 */}
            <div
                style={{
                    display: "flex",
                    gap: "16px",
                    alignItems: "flex-start",
                    marginTop: "8px",
                    fontSize: "14px",
                    color: "#6C757D",
                    width: "100%",
                }}
            >
                <div
                    style={{
                        fontWeight: "700",
                        minWidth: "48px",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        lineHeight: "28px",
                    }}
                >
                    본문
                </div>
                <div style={{ flex: 1 }}>
                    <ReactQuill
                        ref={quillRef}
                        value={contents}
                        onChange={setContents}
                        theme="snow"
                        modules={modules}
                        formats={formats}
                        style={{ height: '100%' }}
                    />
                </div>
            </div>
            <BTBox height="36px" />
            <HorizontalLayout style={{ justifyContent: 'flex-end' }}>
                <BTButton color={theme.colors.bbTech} textColor="white" onClick={handleSave}>저장</BTButton>
                <BTBox width='16px' />
                <BTButton onClick={handleCancel}>취소</BTButton>
            </HorizontalLayout>
        </form>
    );
};

export default BTEmailEditor;